import { useState } from 'react';

import { countries } from 'constants/countries';
import { Controller, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { AnimatedModal } from 'components/AnimatedModal/AnimatedModal';
import {
  Button,
  FilePicker,
  InputGroup,
  Select,
  ToggleSwitchInput,
} from 'components/FormComponents';

export const DocumentUploadModal = ({
  visible,
  onClose = () => {},
  onUpload = async () => {},
  onDualUpload = async () => {},
  title = 'Upload document',
  description,
  selectedType,
  selectedCountry,
  onTypeChange = () => {},
  onCountryChange = () => {},
  options = [],
  metadata = {},
  idDetails = undefined,
  modelParams = {},
  bucketFolder,
  signUpload,
  deleteUpload,
  createUploadAction = false,
  onlyUpload = true,
}) => {
  const { t } = useTranslation();

  const { handleSubmit, control, reset, register, watch, setValue } = useForm({
    defaultValues: {
      selectedType: selectedType,
      is_dual_upload: false,
    },
  });

  const [uploadData, setUploadData] = useState(null);
  const [complementaryUploadData, setComplementaryUploadData] = useState(null);

  const [isDualUpload, setIsDualUpload] = useState(false);

  const setupUploadData = data => {
    setUploadData({
      ...data,
      type: selectedType,
      id_details: idDetails || undefined,
    });
  };

  const setupComplementaryUploadData = data => {
    setComplementaryUploadData({
      ...data,
      type: selectedType,
      id_details: idDetails || undefined,
    });
  };

  const onSubmit = async () => {
    if (!uploadData) {
      return;
    }

    try {
      if (complementaryUploadData) {
        await onDualUpload(uploadData, complementaryUploadData);
      } else {
        await onUpload(uploadData);
      }
      reset();
      onClose();
    } catch (error) {
      console.error(error);
    }
  };

  return (
    <AnimatedModal
      visible={visible}
      onClose={onClose}
      title={title}
      description={description}
      small
      icon="upload">
      {visible && (
        <form onSubmit={handleSubmit(onSubmit)}>
          {!onlyUpload && (
            <>
              <InputGroup title={`${t('common.documentType')}`} className="u-margin-bottom">
                <Select
                  value={selectedType}
                  useSearch={true}
                  useDefault={false}
                  placeholder={t('common.selectPlaceholder')}
                  inputProps={{
                    onChange: e => onTypeChange(e.target.value),
                  }}>
                  {(options || []).map(({ value, label }) => (
                    <Select.Item key={value} value={value}>
                      {label}
                    </Select.Item>
                  ))}
                </Select>
              </InputGroup>
              <InputGroup title={`${t('common.country')}`} className="u-margin-bottom">
                <Select
                  value={selectedCountry}
                  useSearch={true}
                  useDefault={false}
                  placeholder={t('common.selectCountryPlaceholder')}
                  inputProps={{
                    onChange: e => onCountryChange?.(e.target.value),
                  }}>
                  {(countries || []).map(({ alpha2, name }) => (
                    <Select.Item key={alpha2} value={alpha2}>
                      {name}
                    </Select.Item>
                  ))}
                </Select>
              </InputGroup>
              <ToggleSwitchInput
                text="Is the document a dual sided upload?"
                noMaxWidth
                subtext="Enable this to upload a dual sided document. This will upload two documents, one for the front and one for the back of the document."
                onChange={e => setIsDualUpload(e.target.checked)}
                value={isDualUpload}
              />
            </>
          )}
          <InputGroup
            title={isDualUpload ? `${t('common.frontOfDocument')}` : `${t('common.document')}`}
            className="u-margin-top">
            <Controller
              control={control}
              name="upload_id"
              render={({ field: { value } }) => (
                <FilePicker
                  onChange={setupUploadData}
                  value={value}
                  metadata={isDualUpload ? { ...metadata, dual: 'front' } : metadata}
                  modelParams={modelParams}
                  bucketFolder={bucketFolder}
                  signUpload={signUpload}
                  deleteUpload={deleteUpload}
                  createUploadAction={createUploadAction}
                />
              )}
            />
          </InputGroup>

          {isDualUpload && (
            <InputGroup title={`${t('common.backOfDocument')}`} className="u-margin-top">
              <Controller
                control={control}
                name="complementary_upload_id"
                render={({ field: { value } }) => (
                  <FilePicker
                    onChange={setupComplementaryUploadData}
                    value={value}
                    metadata={{ ...metadata, dual: 'back' }}
                    modelParams={modelParams}
                    bucketFolder={bucketFolder}
                    signUpload={signUpload}
                    deleteUpload={deleteUpload}
                    createUploadAction={createUploadAction}
                  />
                )}
              />
            </InputGroup>
          )}
          <Button
            submit
            type="primary"
            large
            className="u-width-100 u-margin-top--large"
            disabled={!onlyUpload && (!selectedType || !selectedCountry)}>
            Upload
          </Button>
        </form>
      )}
    </AnimatedModal>
  );
};
